import * as RadixTooltip from '@radix-ui/react-tooltip';
import type { ReactNode } from 'react';

import { cn } from '~/lib/cn';
import { variants } from '~/lib/variants';

export type TooltipSize = 'md' | 'sm';

export interface TooltipProps {
  triggerAsChild?: boolean;
  content: ReactNode;
  children: ReactNode;
  side?: RadixTooltip.TooltipContentProps['side'];
  delayDuration?: number;
  enabled?: boolean;
  size?: TooltipSize;
  sideOffset?: number;
  align?: RadixTooltip.TooltipContentProps['align'];
  alignOffset?: number;
  open?: RadixTooltip.TooltipProps['open'];
  contentClassName?: string;
  onOpenChange?: RadixTooltip.TooltipProps['onOpenChange'];
}

const tooltip = variants({
  base: [
    'bg-default',
    'text-default',
    'animate-fade',
    'z-50',
    'shadow',
    'border',
    'border-emphasis-subtle',
    'bg-level-01',
    'text-white',
    'break-words',
  ],
  variants: {
    size: {
      md: ['p-md', 'text-body-sm', 'rounded-sm', 'max-w-xs'],
      sm: ['py-xs', 'px-sm', 'text-body-xs', 'rounded-xs'],
    },
  },
});

function Tooltip({
  content,
  children,
  side,
  size = 'md',
  delayDuration,
  enabled = true,
  triggerAsChild = false,
  sideOffset,
  align,
  alignOffset,
  open,
  contentClassName = '',
  onOpenChange,
}: TooltipProps) {
  const sizeClasses = tooltip({ size });

  return (
    <RadixTooltip.Provider>
      <RadixTooltip.Root
        delayDuration={delayDuration}
        open={open}
        onOpenChange={onOpenChange}
      >
        {enabled ? (
          <RadixTooltip.Trigger asChild={triggerAsChild} type="button">
            {children}
          </RadixTooltip.Trigger>
        ) : (
          <>{children}</>
        )}
        <RadixTooltip.Portal>
          <RadixTooltip.Content
            className={cn(tooltip.base, sizeClasses, contentClassName)}
            side={side}
            sideOffset={sideOffset}
            align={align}
            alignOffset={alignOffset}
          >
            {content}
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
}

Tooltip.displayName = 'Tooltip';

export { Tooltip };
